import React, { useEffect, useState } from "react";
import { IconButton, MessageBar, MessageBarType } from "@fluentui/react";
import { File, listFiles } from "@/api/files";
import TagsPanel from "@/components/files/TagsPanel";
import FileUploadPanel from "@/components/files/FileUploadPanel";
import {
  PageRequest,
  OrderByDirection,
  PropertyFilterSet,
  PropertyFilter,
  Comparison,
  BooleanOperator,
} from "@/api/types";
import AddButton from "@/ui-controls/AddButton";
import SearchBox from "@/ui-controls/SearchBox";
import { useErrorNotification } from "@/utils/useErrorNotification";
import FileDetailList from "@/components/files/FileDetailList";
import FileFilterPanel, { FileFilterOptions } from "@/components/files/FileFilterPanel";

const FilesPage = () => {
  const { errorMessage, showError, clearError } = useErrorNotification();
  const [files, setFiles] = useState<File[]>([]);
  const [filteredFiles, setFilteredFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isSearchPerformed, setIsSearchPerformed] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedFilters, setSelectedFilters] = useState<FileFilterOptions>({
    statusFilter: "all",
    selectedCustomer: null,
    fileSizeMin: "",
    fileSizeMax: "",
  });
  const [appliedFilters, setAppliedFilters] = useState<FileFilterOptions>({
    statusFilter: "all",
    selectedCustomer: null,
    fileSizeMin: "",
    fileSizeMax: "",
  });
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState<boolean>(false);
  const [isTagsPanelOpen, setIsTagsPanelOpen] = useState<boolean>(false);
  const [isUploadPanelOpen, setIsUploadPanelOpen] = useState<boolean>(false);
  const [selectedFileId, setSelectedFileId] = useState<number>();

  useEffect(() => {
    // Always use server filtering when page size or page changes
    applyServerFilters();
  }, [pageSize, currentPage]);

  const loadFiles = () => {
    setLoading(true);
    const pageRequest: PageRequest = {
      pageNumber: currentPage,
      pageSize: pageSize,
      orderBy: {
        field: "id",
        direction: OrderByDirection.Desc,
      },
    };

    listFiles(pageRequest)
      .then((result) => {
        // Handle PagedResponse structure
        if (result && result.result && Array.isArray(result.result)) {
          setFiles(result.result);
          setTotalCount(result.totalCount || 0);
        } else {
          setFiles([]);
          setTotalCount(0);
        }
        setLoading(false);
      })
      .catch((error) => {
        handleError(error);
        setFiles([]); // Set empty array on error
        setTotalCount(0);
        setLoading(false);
      });
  };

  const handleError = (error: any) => {
    showError(error);
  };

  const handleFileSearch = (searchValue: string) => {
    setSearchTerm(searchValue);
    setCurrentPage(1); // Reset to first page when searching
    // Trigger immediate search with current applied filters and new search term
    applyServerFilters(undefined, searchValue);
  };

  const handleRefresh = () => {
    setIsSearchPerformed(false);
    setFilteredFiles([]);
    setSearchTerm("");
    setSelectedFilters({
      statusFilter: "all",
      selectedCustomer: null,
      fileSizeMin: "",
      fileSizeMax: "",
    });
    setAppliedFilters({
      statusFilter: "all",
      selectedCustomer: null,
      fileSizeMin: "",
      fileSizeMax: "",
    });
    setCurrentPage(1);
    loadFiles();
  };

  const handleFiltersChange = (filters: FileFilterOptions) => {
    console.log("FilesPage handleFiltersChange - received filters:", filters);
    console.log(
      "FilesPage handleFiltersChange - current selectedFilters before update:",
      selectedFilters
    );
    setSelectedFilters(filters);
  };

  const handleApplyFilters = (filters: FileFilterOptions) => {
    console.log("FilesPage handleApplyFilters - received filters:", filters);
    console.log(
      "FilesPage handleApplyFilters - selectedFilters at time of apply:",
      selectedFilters
    );
    setAppliedFilters(filters);
    applyServerFilters(filters);
  };

  const applyServerFilters = (
    filters?: FileFilterOptions,
    searchValue?: string
  ) => {
    const filtersToUse = filters || appliedFilters;
    const searchToUse = searchValue !== undefined ? searchValue : searchTerm;

    const filterSet: PropertyFilterSet = {
      operator: BooleanOperator.And,
      filters: [] as PropertyFilter[],
    };

    if (searchToUse.trim()) {
      filterSet.filters.push({
        field: "fileName",
        comparison: Comparison.Contains,
        value: searchToUse,
      });
    }

    if (filtersToUse.statusFilter !== "all") {
      filterSet.filters.push({
        field: "isActive",
        comparison: Comparison.Equals,
        value: filtersToUse.statusFilter === "active" ? "true" : "false",
      });
    }

    if (filtersToUse.selectedCustomer) {
      filterSet.filters.push({
        field: "customerId",
        comparison: Comparison.Equals,
        value: String(filtersToUse.selectedCustomer.Id),
      });
    }

    // Add file size filters directly to main filter set (AND logic)
    if (filtersToUse.fileSizeMin.trim()) {
      filterSet.filters.push({
        field: "fileSizeInKb",
        comparison: Comparison.GreaterThanOrEqual,
        value: filtersToUse.fileSizeMin.trim(),
      });
    }
    if (filtersToUse.fileSizeMax.trim()) {
      filterSet.filters.push({
        field: "fileSizeInKb",
        comparison: Comparison.LessThanOrEqual,
        value: filtersToUse.fileSizeMax.trim(),
      });
    }

    const pageRequest: PageRequest = {
      pageNumber: currentPage,
      pageSize: pageSize,
      orderBy: {
        field: "id",
        direction: OrderByDirection.Desc,
      },
      filterSet,
    };

    setLoading(true);

    listFiles(pageRequest)
      .then((result) => {
        if (result && result.result && Array.isArray(result.result)) {
          setFiles(result.result);
          setTotalCount(result.totalCount || 0);
        } else {
          setFiles([]);
          setTotalCount(0);
        }
        setLoading(false);
      })
      .catch((error) => {
        handleError(error);
        setFiles([]);
        setTotalCount(0);
        setLoading(false);
      });
  };

  const handleClearFilters = () => {
    setSelectedFilters({
      statusFilter: "all",
      selectedCustomer: null,
      fileSizeMin: "",
      fileSizeMax: "",
    });
    setAppliedFilters({
      statusFilter: "all",
      selectedCustomer: null,
      fileSizeMin: "",
      fileSizeMax: "",
    });
  };

  const openFilterPanel = () => {
    setIsFilterPanelOpen(true);
  };

  const closeFilterPanel = () => {
    setIsFilterPanelOpen(false);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const getDisplayFiles = () => {
    return files;
  };

  const getTotalPages = () => {
    return Math.ceil(totalCount / pageSize);
  };

  return (
    <>
      <div className="page-header">
        <div className="breadcrumb-left-section">
          <div>
            <h2 className="page-breadcrumb-title">Files</h2>
            <div style={{ color: "#605e5c", fontSize: "14px" }}>
              Manage file uploads and downloads
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <AddButton
            text="File"
            onClick={() => {
              setIsUploadPanelOpen(true);
            }}
          />
          <SearchBox
            placeholder="Search files..."
            onSearch={handleFileSearch}
            disabled={loading}
          />
          <div
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
            }}
          >
            <IconButton
              iconProps={{ iconName: "Filter" }}
              title="Filter files"
              ariaLabel="Filter files"
              onClick={openFilterPanel}
              disabled={loading}
              styles={{
                root: {
                  width: 40,
                  height: 40,
                  backgroundColor:
                    appliedFilters.statusFilter !== "all" ||
                    appliedFilters.selectedCustomer ||
                    appliedFilters.fileSizeMin ||
                    appliedFilters.fileSizeMax
                      ? "#deecf9"
                      : "transparent",
                },
                rootHovered: { backgroundColor: "#f3f2f1" },
                icon: {
                  fontSize: 18,
                  color:
                    appliedFilters.statusFilter !== "all" ||
                    appliedFilters.selectedCustomer ||
                    appliedFilters.fileSizeMin ||
                    appliedFilters.fileSizeMax
                      ? "#0078d4"
                      : loading
                      ? "#a19f9d"
                      : "#0078d4",
                },
                rootPressed: { backgroundColor: "#e1dfdd" },
                rootDisabled: { backgroundColor: "transparent" },
              }}
            />
            {(appliedFilters.statusFilter !== "all" ||
              appliedFilters.selectedCustomer ||
              appliedFilters.fileSizeMin ||
              appliedFilters.fileSizeMax) && (
              <div
                style={{
                  position: "absolute",
                  top: "-2px",
                  right: "-2px",
                  backgroundColor: "#d13438",
                  color: "white",
                  borderRadius: "50%",
                  width: "16px",
                  height: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "10px",
                  fontWeight: "600",
                  zIndex: 1,
                }}
              >
                1
              </div>
            )}
          </div>
          <IconButton
            iconProps={{ iconName: "Refresh" }}
            title="Refresh"
            ariaLabel="Refresh"
            onClick={handleRefresh}
            disabled={loading}
            styles={{
              root: { width: 40, height: 40 },
              rootHovered: { backgroundColor: "#f3f2f1" },
              icon: {
                fontSize: 18,
                color: loading ? "#a19f9d" : "#0078d4",
              },
              rootPressed: { backgroundColor: "#e1dfdd" },
              rootDisabled: { backgroundColor: "transparent" },
            }}
          />
        </div>
      </div>

      <FileFilterPanel
        isOpen={isFilterPanelOpen}
        onDismiss={closeFilterPanel}
        selectedFilters={selectedFilters}
        onFiltersChange={handleFiltersChange}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
      />

      <div>
        <FileDetailList
          files={getDisplayFiles()}
          loading={loading}
          pageSize={pageSize}
          currentPage={currentPage}
          totalPages={getTotalPages()}
          totalCount={totalCount}
          onPageSizeChange={handlePageSizeChange}
          onPageChange={handlePageChange}
          onRefresh={loadFiles}
          isServerPagination={true}
          onShowTags={(file) => {
            setSelectedFileId(file.id);
            setIsTagsPanelOpen(true);
          }}
          onDelete={() => {
            // Refresh the files list after deletion
            applyServerFilters();
          }}
        />

        <TagsPanel
          fileId={selectedFileId}
          isOpen={isTagsPanelOpen}
          onDismiss={() => setIsTagsPanelOpen(false)}
        />
        <FileUploadPanel
          isOpen={isUploadPanelOpen}
          onDismiss={() => setIsUploadPanelOpen(false)}
          onUploadSuccess={() => {
            setIsUploadPanelOpen(false);
            handleRefresh();
          }}
        />
      </div>
      {errorMessage && (
        <MessageBar
          messageBarType={MessageBarType.error}
          isMultiline={false}
          onDismiss={clearError}
          styles={{
            root: {
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "100%",
              zIndex: 1000,
            },
          }}
        >
          {errorMessage}
        </MessageBar>
      )}
    </>
  );
};

export default FilesPage;
